import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { Link } from "react-router";
import { ApiError, del, get, put } from "../api/client";
import type { Assignee, MeTeam, Player, PlayerMerge, PlayerRef, Roster, RosterAssignmentInfo, UserSearchResult } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useCardDrag, type CardDrag } from "../lib/cardDrag";
import { useIsDesktop } from "../lib/device";
import { plural } from "../lib/format";
import { playerLabel, type ResolvedRoster } from "../lib/roster";
import { cleanHandleInput, HANDLE_RE, initialsOf } from "./HandleSheet";
import { Face, Sheet } from "./ui";

/** The API's limit on a name (`PLAYER_NAME_MAX`, apps/api/src/routes/players.ts). */
export const NAME_MAX = 40;

/**
 * Two players as the page writes them: the @handles or typed names, or the stage's labels
 * when they have none or the same name ("Rohan into Rohan" says nothing).
 */
export function twoNames(
  a: Pick<PlayerRef, "role" | "order"> & { name?: string | null; assignee?: Assignee | null },
  b: Pick<PlayerRef, "role" | "order"> & { name?: string | null; assignee?: Assignee | null },
): [string, string] {
  const disp = (p: Pick<PlayerRef, "role" | "order"> & { name?: string | null; assignee?: Assignee | null }) =>
    p.assignee?.handle ? `@${p.assignee.handle}` : p.name || playerLabel(p);
  const x = disp(a);
  const y = disp(b);
  return x === y ? [playerLabel(a), playerLabel(b)] : [x, y];
}

export function PlayerRows({
  roster,
  videoId,
  onOpen,
  onWarm,
  onRename,
  onMerge,
  onUndo,
  onRosterChange,
  say,
  merging = true,
}: {
  roster: ResolvedRoster;
  videoId?: string;
  onOpen: (p: Player) => void;
  onWarm?: (p: Player) => void;
  onRename: (p: Player, name: string | null) => Promise<void>;
  /** merge `p` into `into`; true once the roster shows it */
  onMerge: (p: Player, into: Player) => Promise<boolean>;
  /** undo the newest merge; true once the roster shows it */
  onUndo: (m: PlayerMerge) => Promise<boolean>;
  onRosterChange?: (r: Roster) => void;
  say?: (msg: string) => void;
  /** false: no dragging one card onto another (a live session's roster is regrouped as segments arrive) */
  merging?: boolean;
}) {
  const desktop = useIsDesktop();
  const { me, refresh } = useAuth();
  const managedTeams = (me?.teams ?? []).filter((t) => t.role === "manager");
  const byId = new Map([...roster.batsmen, ...roster.bowlers].map((p) => [p.id, p]));
  const [ask, setAsk] = useState<{ p: Player; into: Player } | null>(null);
  const [busy, setBusy] = useState<"merge" | "undo" | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { drag, bind } = useCardDrag((from, into) => {
    const p = byId.get(from);
    const q = byId.get(into);
    if (p && q) setAsk({ p, into: q });
  }, merging && !busy && !ask && !editingId);
  useEffect(() => {
    if (!flash) return;
    document.querySelector(`[data-player="${flash}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    const t = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [flash]);
  const merge = async () => {
    if (!ask) return;
    setBusy("merge");
    const ok = await onMerge(ask.p, ask.into);
    setBusy(null);
    setAsk(null);
    if (ok) setFlash(ask.into.id);
  };
  const undo = async (m: PlayerMerge) => {
    setBusy("undo");
    const ok = await onUndo(m);
    setBusy(null);
    if (ok) setFlash(m.player.id);
  };
  const dragged = drag ? byId.get(drag.id) : undefined;
  const canMerge = merging && (roster.batsmen.length > 1 || roster.bowlers.length > 1);
  const notMine = roster.assignment?.not_mine ?? [];
  const cards = {
    videoId,
    assignment: roster.assignment,
    managedTeams,
    onOpen,
    onWarm,
    onRename,
    onRosterChange: async (r: Roster) => {
      onRosterChange?.(r);
      await refresh();
    },
    onEditChange: (id: string, open: boolean) => setEditingId((cur) => (open ? id : cur === id ? null : cur)),
    say,
    drag,
    flash,
    bind,
  };
  return (
    <>
      {videoId && notMine.length > 0 && (
        <div className="inv-banner mt16" data-testid="not-mine-banner">
          <div className="tiny">
            <b>{plural(notMine.length, "ball")}</b> marked "not mine" by{" "}
            {Array.from(new Set(notMine.map((n) => (n.by.handle ? `@${n.by.handle}` : "a player")))).join(", ")}
          </div>
          <Link className="tiny u" to={`/videos/${videoId}/clips?unassigned=1`} data-testid="review-not-mine-link">
            Review unassigned
          </Link>
        </div>
      )}
      <PlayerRow title="Batsmen" players={roster.batsmen} {...cards} />
      <PlayerRow title="Bowlers" players={roster.bowlers} {...cards} />
      <UndoMerge merges={roster.merges} busy={busy} onUndo={undo} />
      {canMerge && (
        <p className="tiny mt16" data-testid="merge-hint">
          {desktop ? "Same player twice? Drag one picture onto the other." : "Same player twice? Hold one picture, then drag it onto the other."}
        </p>
      )}
      {drag && dragged && createPortal(<Ghost p={dragged} into={drag.over ? byId.get(drag.over) : undefined} drag={drag} />, document.body)}
      {ask && <MergeSheet p={ask.p} into={ask.into} busy={busy === "merge"} onMerge={merge} onCancel={() => setAsk(null)} />}
    </>
  );
}

/** While the players are being found: one row of grey cards where they will be. */
export function PlayerRowsLoading() {
  return (
    <section className="mt32" data-testid="players-loading">
      <div className="prow-h"><h2>Players</h2></div>
      <div className="prow">
        {Array.from({ length: 6 }, (_, k) => (
          <div key={k} className="pc sk" aria-hidden="true"><div className="pic" /><div className="nt" /></div>
        ))}
      </div>
    </section>
  );
}

type CardBind = ReturnType<typeof useCardDrag>["bind"];

function PlayerRow({
  title,
  players,
  videoId,
  assignment,
  managedTeams,
  onOpen,
  onWarm,
  onRename,
  onRosterChange,
  onEditChange,
  say,
  drag,
  flash,
  bind,
}: {
  title: string;
  players: Player[];
  videoId?: string | undefined;
  assignment?: RosterAssignmentInfo | null | undefined;
  managedTeams: MeTeam[];
  onOpen: (p: Player) => void;
  onWarm?: ((p: Player) => void) | undefined;
  onRename: (p: Player, name: string | null) => Promise<void>;
  onRosterChange?: ((r: Roster) => void | Promise<void>) | undefined;
  onEditChange?: ((id: string, open: boolean) => void) | undefined;
  say?: ((msg: string) => void) | undefined;
  drag: CardDrag | null;
  flash: string | null;
  bind: CardBind;
}) {
  const desktop = useIsDesktop();
  const row = useRef<HTMLDivElement | null>(null);
  const [all, setAll] = useState(false);
  const [edge, setEdge] = useState({ start: true, end: true });
  const measure = useCallback(() => {
    const el = row.current;
    if (el) setEdge({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, players.length, all]);
  const page = (dir: 1 | -1) => row.current?.scrollBy({ left: dir * row.current.clientWidth * 0.9, behavior: "smooth" });
  if (players.length === 0) return null;
  const overflows = !(edge.start && edge.end);
  return (
    <section className="mt32" data-testid={`players-${title.toLowerCase()}`}>
      <div className="prow-h">
        <h2>{title}</h2>
        <span className="tiny">{players.length}</span>
        <span className="sp" />
        {(overflows || all) && <button type="button" className="u more" onClick={() => setAll((a) => !a)} data-testid="see-all">{all ? "Show fewer" : `See all ${players.length}`}</button>}
        {desktop && overflows && !all && (
          <>
            <button type="button" className="arr" aria-label={`Earlier ${title.toLowerCase()}`} disabled={edge.start} onClick={() => page(-1)}>‹</button>
            <button type="button" className="arr" aria-label={`More ${title.toLowerCase()}`} disabled={edge.end} onClick={() => page(1)}>›</button>
          </>
        )}
      </div>
      <div ref={row} className={`prow${all ? " all" : ""}`} onScroll={measure}>
        {players.map((p) => (
          <PlayerCard
            key={p.id}
            p={p}
            videoId={videoId}
            assignment={assignment}
            managedTeams={managedTeams}
            onOpen={() => onOpen(p)}
            onWarm={onWarm ? () => onWarm(p) : undefined}
            onRename={(name) => onRename(p, name)}
            onRosterChange={onRosterChange}
            onEditChange={(open) => onEditChange?.(p.id, open)}
            say={say}
            state={drag?.id === p.id ? "lifted" : drag?.over === p.id ? "target" : flash === p.id ? "flash" : null}
            bind={bind}
          />
        ))}
      </div>
    </section>
  );
}

function PlayerCard({
  p,
  videoId,
  assignment,
  managedTeams,
  onOpen,
  onWarm,
  onRename,
  onRosterChange,
  onEditChange,
  say,
  state,
  bind,
}: {
  p: Player;
  videoId?: string | undefined;
  assignment?: RosterAssignmentInfo | null | undefined;
  managedTeams: MeTeam[];
  onOpen: () => void;
  onWarm?: (() => void) | undefined;
  onRename: (name: string | null) => Promise<void>;
  onRosterChange?: ((r: Roster) => void | Promise<void>) | undefined;
  onEditChange?: ((open: boolean) => void) | undefined;
  say?: ((msg: string) => void) | undefined;
  state: "lifted" | "target" | "flash" | null;
  bind: CardBind;
}) {
  const label = playerLabel(p);
  const shownName = p.assignee?.handle ? `@${p.assignee.handle}` : p.name || label;
  return (
    <div className={`pc${state ? ` ${state}` : ""}`} data-testid="player-card" data-role={p.role} data-player={p.id} {...bind}>
      <button type="button" className="pic" onClick={onOpen} onPointerEnter={onWarm} onTouchStart={onWarm} aria-label={`Play the ${plural(p.clip_count, "clip")} of ${shownName}`} data-testid="player-open">
        <Face id={p.id} src={p.crop_url} css fill />
        <span className="ct"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3.5l15 8.5-15 8.5z" /></svg>{plural(p.clip_count, "clip")}</span>
        {state === "target" && <span className="mg" data-testid="merge-target" />}
      </button>
      <NameTag
        p={p}
        label={label}
        videoId={videoId}
        assignment={assignment}
        managedTeams={managedTeams}
        onSave={onRename}
        onRosterChange={onRosterChange}
        onEditChange={onEditChange}
        say={say}
      />
    </div>
  );
}

function Ghost({ p, into, drag }: { p: Player; into: Player | undefined; drag: CardDrag }) {
  const below = drag.y < 240;
  const gap = drag.touch ? 36 : 18;
  const [from, to] = into ? twoNames(p, into) : [p.assignee?.handle ? `@${p.assignee.handle}` : p.name || playerLabel(p), ""];
  return (
    <div className={`pc ghost${below ? " below" : ""}`} style={{ left: drag.x, top: below ? drag.y + gap : drag.y - gap }} aria-hidden="true" data-testid="drag-ghost">
      <div className="pic">
        <Face id={p.id} src={p.crop_url} css fill />
        <span className="ct">{plural(p.clip_count, "clip")}</span>
      </div>
      {into ? (
        <div className="nt into" data-testid="drag-ghost-tag"><span>Merge into</span><b>{to}</b></div>
      ) : (
        <div className={`nt${p.name || p.assignee ? "" : " none"}`} data-testid="drag-ghost-tag"><span>{from}</span></div>
      )}
    </div>
  );
}

function MergeSheet({ p, into, busy, onMerge, onCancel }: { p: Player; into: Player; busy: boolean; onMerge: () => void; onCancel: () => void }) {
  const [from, to] = twoNames(p, into);
  const kept = into.assignee?.handle ? `@${into.assignee.handle}` : into.name || (p.assignee?.handle ? `@${p.assignee.handle}` : p.name) || playerLabel(into);
  return (
    <Sheet onClose={busy ? () => {} : onCancel}>
      <div data-testid="merge-sheet">
        <h2>Merge {from} into {to}?</h2>
        <div className="merge-pair mt16" aria-hidden="true">
          <figure><Face id={p.id} src={p.crop_url} css fill /><figcaption><b>{from}</b>{plural(p.clip_count, "clip")}</figcaption></figure>
          <svg className="to" viewBox="0 0 24 24"><path d="M4 12h15M13 6l6 6-6 6" /></svg>
          <figure><Face id={into.id} src={into.crop_url} css fill /><figcaption><b>{to}</b>{plural(into.clip_count, "clip")}</figcaption></figure>
        </div>
        <p className="sub mt16" data-testid="merge-result">They become one player, {kept}, with {plural(p.clip_count + into.clip_count, "clip")}.</p>
        <div className="mt24"><button className="btn dark full" onClick={onMerge} disabled={busy} autoFocus data-testid="merge-confirm">{busy ? "Merging…" : "Merge"}</button></div>
        <div className="mt10"><button className="btn ghost full" onClick={onCancel} disabled={busy} data-testid="merge-cancel">Cancel</button></div>
        <p className="tiny center mt10">Undo merge takes it back.</p>
      </div>
    </Sheet>
  );
}

function UndoMerge({ merges, busy, onUndo }: { merges: PlayerMerge[]; busy: "merge" | "undo" | null; onUndo: (m: PlayerMerge) => void }) {
  const m = merges[0];
  if (!m) return null;
  const [from, to] = twoNames(m.player, m.into);
  const more = merges.length - 1;
  return (
    <div className="undo-merge mt16" data-testid="undo-merge-bar">
      <button type="button" className="btn ghost sm" onClick={() => onUndo(m)} disabled={busy !== null} data-testid="undo-merge">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14L4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></svg>
        {busy === "undo" ? "Undoing…" : "Undo merge"}
      </button>
      <span className="tiny" data-testid="undo-merge-what">{from} into {to}{more ? ` · ${more} more to undo` : ""}</span>
    </div>
  );
}

function NameTag({
  p,
  label,
  videoId,
  assignment,
  managedTeams,
  onSave,
  onRosterChange,
  onEditChange,
  say,
}: {
  p: Player;
  label: string;
  videoId?: string | undefined;
  assignment?: RosterAssignmentInfo | null | undefined;
  managedTeams: MeTeam[];
  onSave: (name: string | null) => Promise<void>;
  onRosterChange?: ((r: Roster) => void | Promise<void>) | undefined;
  onEditChange?: ((open: boolean) => void) | undefined;
  say?: ((msg: string) => void) | undefined;
}) {
  const name = p.name ?? null;
  const assignee = p.assignee ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const [search, setSearch] = useState<UserSearchResult | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [linkTeamId, setLinkTeamId] = useState<string>(() => managedTeams[0]?.id ?? "");
  const [newTeamName, setNewTeamName] = useState("");
  const [createNewTeam, setCreateNewTeam] = useState(managedTeams.length === 0);
  const [comboPos, setComboPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 268 });
  const [assignBusy, setAssignBusy] = useState(false);

  const closed = useRef(false);
  const box = useRef<HTMLInputElement | null>(null);
  const comboRef = useRef<HTMLDivElement | null>(null);
  const insideCombo = useRef(false);

  const teamId = assignment?.team_id ?? null;
  const trimmed = draft.trim();
  const isHandleMode = trimmed.startsWith("@");
  const slug = cleanHandleInput(trimmed);
  const showCombo = editing && !!videoId && (isHandleMode || !!assignee || inviteOpen);

  const updatePos = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(280, window.innerWidth - 24);
    const left = Math.max(12, Math.min(r.left, window.innerWidth - w - 12));
    const comboH = comboRef.current?.offsetHeight || 220;
    const rawTop = r.bottom + 6 + comboH > window.innerHeight && r.top > comboH + 18 ? r.top - comboH - 6 : r.bottom + 6;
    const top = Math.max(12, Math.min(rawTop, window.innerHeight - Math.min(comboH, 260) - 12));
    setComboPos({ top, left, width: w });
  }, []);

  useEffect(() => {
    if (!showCombo) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [showCombo, search, inviteOpen, updatePos]);

  useEffect(() => {
    if (!editing || !videoId || !teamId || !isHandleMode) {
      setSearch(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      get<UserSearchResult>(`/v1/users/search?q=${encodeURIComponent(slug)}&team_id=${encodeURIComponent(teamId)}`)
        .then((res) => {
          if (!cancelled) setSearch(res);
        })
        .catch(() => {});
    }, 140);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [editing, videoId, teamId, isHandleMode, slug]);

  const stopEdit = () => {
    closed.current = true;
    setEditing(false);
    setInviteOpen(false);
    setInviteEmail("");
    onEditChange?.(false);
  };

  const open = () => {
    closed.current = false;
    insideCombo.current = false;
    setInviteOpen(false);
    setInviteEmail("");
    setLinkTeamId(managedTeams[0]?.id ?? "");
    setCreateNewTeam(managedTeams.length === 0);
    flushSync(() => {
      setDraft(assignee?.handle ? `@${assignee.handle}` : (name ?? ""));
      setEditing(true);
    });
    onEditChange?.(true);
    box.current?.focus();
  };

  const assignTarget = async (body: Record<string, unknown>, labelToast: string) => {
    if (!videoId || assignBusy) return;
    setAssignBusy(true);
    try {
      const updated = await put<Roster>(`/v1/videos/${videoId}/players/${p.id}/assignment`, body);
      stopEdit();
      await onRosterChange?.(updated);
      say?.(labelToast);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404 && e.details?.reason === "UNKNOWN_HANDLE") {
        setInviteOpen(true);
      } else {
        say?.((e as Error).message);
      }
    } finally {
      setAssignBusy(false);
    }
  };

  const unassign = async () => {
    if (!videoId || assignBusy) return;
    setAssignBusy(true);
    try {
      const updated = await del<Roster>(`/v1/videos/${videoId}/players/${p.id}/assignment`);
      stopEdit();
      await onRosterChange?.(updated);
      say?.(`Unassigned ${label}.`);
    } catch (e) {
      say?.((e as Error).message);
    } finally {
      setAssignBusy(false);
    }
  };

  const closePlainOrEnter = (save: boolean, fromEnter = false) => {
    if (closed.current) return;
    if (!save) {
      stopEdit();
      return;
    }
    if (isHandleMode && videoId) {
      if (!fromEnter) {
        // Blur while typing a @handle: if user didn't pick from combo, only close if not inside combo
        stopEdit();
        return;
      }
      if (!slug) {
        stopEdit();
        return;
      }
      if (!teamId) {
        // Needs team link first — keep combo open so user can click Link & assign
        return;
      }
      void assignTarget({ handle: slug }, `Assigned ${p.clip_count} clips to @${slug}`);
      return;
    }
    stopEdit();
    const next = draft.replace(/\s+/g, " ").trim();
    if (next === (name ?? "")) return;
    setSaving(next);
    void onSave(next || null).finally(() => setSaving(null));
  };

  useEffect(() => {
    if (!editing) return;
    const onDocDown = (ev: PointerEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;
      if (box.current?.contains(target) || comboRef.current?.contains(target)) return;
      closePlainOrEnter(true, false);
    };
    document.addEventListener("pointerdown", onDocDown, true);
    return () => document.removeEventListener("pointerdown", onDocDown, true);
  });

  if (editing) {
    const exactKnown =
      !!slug &&
      ((search?.team_members ?? []).some((m) => m.handle === slug) ||
        (search?.other_users ?? []).some((u) => u.handle === slug) ||
        (search?.pending_invites ?? []).some((i) => i.handle === slug));
    const canOfferInvite = search ? search.can_invite : HANDLE_RE.test(slug) && !exactKnown;

    return (
      <>
        <input
          ref={box}
          className={`nt${isHandleMode ? " editing" : ""}`}
          value={draft}
          enterKeyHint="done"
          autoCapitalize={isHandleMode ? "none" : "words"}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={NAME_MAX}
          placeholder={label}
          aria-label={`Name for ${label}`}
          data-testid="name-input"
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            setDraft(e.target.value);
            setInviteOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              closePlainOrEnter(true, true);
            } else if (e.key === "Escape") {
              e.preventDefault();
              closePlainOrEnter(false);
            }
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (insideCombo.current) {
                insideCombo.current = false;
                return;
              }
              closePlainOrEnter(true, false);
            }, 120);
          }}
        />
        {showCombo &&
          createPortal(
            <div
              ref={comboRef}
              className="combo"
              style={{ top: comboPos.top, left: comboPos.left, width: comboPos.width }}
              data-testid="handle-combo"
              onMouseDown={(e) => {
                insideCombo.current = true;
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                insideCombo.current = true;
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {!teamId ? (
                <div className="combo-invite" data-testid="combo-link-team">
                  <div className="med">Link match to a team to assign @{slug || "handle"}</div>
                  <p className="tiny mt4">Player @handle assignments belong to a team.</p>
                  {managedTeams.length > 0 && !createNewTeam ? (
                    <>
                      <select
                        className="field mt8"
                        value={linkTeamId}
                        onChange={(e) => setLinkTeamId(e.target.value)}
                        data-testid="combo-team-select"
                      >
                        {managedTeams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="tiny u mt6"
                        onClick={() => setCreateNewTeam(true)}
                        data-testid="combo-new-team-toggle"
                      >
                        + Create new team instead
                      </button>
                    </>
                  ) : (
                    <input
                      className="field mt8"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      placeholder="New team name (e.g. Vikings XI)"
                      data-testid="combo-new-team-input"
                    />
                  )}
                  {inviteOpen && (
                    <input
                      type="email"
                      className="field mt8"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={`Email for @${slug}`}
                      data-testid="combo-invite-email"
                    />
                  )}
                  <div className="row mt10" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="btn dark sm full"
                      disabled={assignBusy || !slug || (createNewTeam ? !newTeamName.trim() : !linkTeamId) || (inviteOpen && !inviteEmail.trim())}
                      onClick={() => {
                        const teamPart = createNewTeam ? { new_team_name: newTeamName.trim() } : { team_id: linkTeamId };
                        if (inviteOpen) {
                          void assignTarget(
                            { invite: { handle: slug, email: inviteEmail.trim() }, ...teamPart },
                            `Invited @${slug} and assigned ${p.clip_count} clips`,
                          );
                        } else {
                          void assignTarget({ handle: slug, ...teamPart }, `Linked match & assigned ${p.clip_count} clips to @${slug}`);
                        }
                      }}
                      data-testid="combo-link-assign"
                    >
                      {assignBusy ? "Saving…" : inviteOpen ? "Link, invite & assign" : "Link & assign"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {search && search.team_members.length > 0 && (
                    <>
                      <div className="combo-sec">Team members</div>
                      {search.team_members.map((m) => (
                        <button
                          key={m.user_id}
                          type="button"
                          className="combo-row"
                          onClick={() =>
                            m.handle &&
                            assignTarget({ user_id: m.user_id }, `Assigned ${p.clip_count} clips to @${m.handle}`)
                          }
                          data-testid={`combo-member-${m.handle ?? m.user_id}`}
                        >
                          <span className="combo-av">{initialsOf(m.display_name, m.handle)}</span>
                          <div>
                            <div className="h">@{m.handle}</div>
                            <div className="n">{m.display_name}</div>
                          </div>
                          <span className="r">{m.role === "manager" ? "Manager" : "Member"}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {search && search.pending_invites.length > 0 && (
                    <>
                      <div className="combo-sec">Pending invites</div>
                      {search.pending_invites.map((inv) => (
                        <button
                          key={inv.invite_id}
                          type="button"
                          className="combo-row"
                          onClick={() =>
                            assignTarget({ invite_id: inv.invite_id }, `Assigned ${p.clip_count} clips to @${inv.handle}`)
                          }
                          data-testid={`combo-pending-${inv.handle}`}
                        >
                          <span className="combo-av">@</span>
                          <div className="h">@{inv.handle}</div>
                          <span className="r">Invited</span>
                        </button>
                      ))}
                    </>
                  )}
                  {search && search.other_users.length > 0 && (
                    <>
                      <div className="combo-sec">Other Deep Third users</div>
                      {search.other_users.map((u) => (
                        <button
                          key={u.user_id}
                          type="button"
                          className="combo-row"
                          onClick={() =>
                            u.handle &&
                            assignTarget({ user_id: u.user_id }, `Assigned ${p.clip_count} clips to @${u.handle}`)
                          }
                          data-testid={`combo-user-${u.handle ?? u.user_id}`}
                        >
                          <span className="combo-av">{initialsOf(u.display_name, u.handle)}</span>
                          <div>
                            <div className="h">@{u.handle}</div>
                            <div className="n">{u.display_name}</div>
                          </div>
                          <span className="r">+ Add to team</span>
                        </button>
                      ))}
                    </>
                  )}
                  {canOfferInvite && !inviteOpen && (
                    <button
                      type="button"
                      className="combo-row combo-inv"
                      onClick={() => setInviteOpen(true)}
                      data-testid="combo-invite-trigger"
                    >
                      <span className="combo-av" style={{ background: "var(--blue)" }}>+</span>
                      <div>
                        <div className="h">Invite @{slug} to Deep Third</div>
                        <div className="n">Send email invite & assign {p.clip_count} clips</div>
                      </div>
                    </button>
                  )}
                  {inviteOpen && (
                    <div className="combo-invite" data-testid="combo-invite-box">
                      <div className="med">Invite @{slug}</div>
                      <div className="tiny mt4">We email them a link to claim @{slug} and watch these {p.clip_count} clips free.</div>
                      <input
                        type="email"
                        className="field mt8"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder={`${slug}@gmail.com`}
                        data-testid="combo-invite-email"
                      />
                      <button
                        type="button"
                        className="btn dark sm full mt8"
                        disabled={assignBusy || !inviteEmail.trim()}
                        onClick={() =>
                          assignTarget(
                            { invite: { handle: slug, email: inviteEmail.trim() } },
                            `Invite sent to ${inviteEmail.trim()} · ${p.clip_count} clips assigned to @${slug}`,
                          )
                        }
                        data-testid="combo-invite-send"
                      >
                        {assignBusy ? "Sending…" : `Send invite & assign ${p.clip_count} clips`}
                      </button>
                    </div>
                  )}
                  {assignee && (
                    <button
                      type="button"
                      className="combo-row"
                      onClick={unassign}
                      data-testid="combo-unassign"
                    >
                      <span className="bad">Unassign {assignee.handle ? `@${assignee.handle}` : label}</span>
                    </button>
                  )}
                </>
              )}
            </div>,
            document.body,
          )}
      </>
    );
  }

  if (assignee) {
    const isInvite = assignee.kind === "invite";
    const h = assignee.handle ? `@${assignee.handle}` : assignee.kind === "user" ? assignee.display_name : label;
    return (
      <button
        type="button"
        className={`nt ${isInvite ? "invited" : "handle"}`}
        onClick={open}
        title={`${h}: click to change or unassign`}
        data-testid="name-tag"
      >
        {isInvite && <span className="dot on" />}
        <span>{h}</span>
        {isInvite ? (
          <span className="tiny" style={{ marginLeft: "auto" }}>Invited</span>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
        )}
      </button>
    );
  }

  const shown = saving !== null ? saving || label : name || label;
  const named = saving !== null ? !!saving : !!name;
  return (
    <button
      type="button"
      className={`nt${named ? "" : " none"}`}
      onClick={open}
      title={named ? `${shown}: click to rename` : `Click to name ${label}`}
      data-testid="name-tag"
    >
      <span>{shown}</span>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
    </button>
  );
}
